import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ScheduleBlockEntryForm } from "Bands/components";
import {
  BAND_ACTIONS,
  IScheduleBlockEntry,
  IScheduleBlockEntryParams,
  IScheduleBlockEntryProperties,
} from "Bands/types";
import {
  clearActionMeta,
  dataMetaSelector,
  dispatchGetEventScheduleBlock,
  editEventScheduleBlockEntry,
} from "Bands/state";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { Modal } from "shared/components";
import { utcStringToDateTimeTimezone } from "shared/utils";
import { Clock } from "shared/assets/icons";
import { getMemberName } from "Bands/utils";

const scheduleBlockCopy = copy.scheduleBlock.editEntry;

interface EditScheduleBlockEntryModalProps {
  blockId: string;
  entry: IScheduleBlockEntry;
  handleClose: () => void;
}

export const EditScheduleBlockEntryModal: FC<
  EditScheduleBlockEntryModalProps
> = ({ blockId, entry, handleClose }) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editEventScheduleBlockEntry)
  );

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.editEventScheduleBlockEntry));

      handleClose();

      // refetch block
      dispatchGetEventScheduleBlock(dispatch, blockId);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: scheduleBlockCopy.messages.edited,
        })
      );
    }
  }, [apiMeta, blockId, dispatch, handleClose]);

  const handleSubmit = ({
    entry: data,
  }: {
    entry: IScheduleBlockEntryParams;
  }) => {
    dispatch(editEventScheduleBlockEntry({ id: entry.id, data }));
  };

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: {
          "data-testid": "submit_edit_entry",
          tabIndex: 8,
        },
        secondary: {
          "data-testid": "cancel_edit_entry",
          onClick: handleClose,
        },
      } as IFormButtons),
    [handleClose]
  );

  const defaultValues = useMemo(() => {
    const { start, end, users, ...rest } = entry;
    const { date: startDate, time: startTime } =
      utcStringToDateTimeTimezone(start);
    const { date: endDate, time: endTime } = utcStringToDateTimeTimezone(end);

    return {
      ...rest,
      startDate,
      startTime,
      endDate,
      endTime,
      users: users?.map(({ id, ...rest }) => ({
        value: id,
        label: getMemberName(rest),
      })),
    } as IScheduleBlockEntryProperties;
  }, [entry]);

  return (
    <Modal
      icon={Clock}
      title={scheduleBlockCopy.title}
      dataTestId="edit_entry_modal"
    >
      <ScheduleBlockEntryForm
        error={!!apiMeta?.error}
        apiStatus={apiMeta?.status}
        handleSubmit={handleSubmit}
        buttons={buttons}
        apiError={apiMeta?.error}
        defaultValues={defaultValues}
        isEdit={true}
        showEditEntryModal={entry}
      />
    </Modal>
  );
};
